import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Skeleton,
  InputAdornment,
  tableCellClasses,
  styled,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#0b1e3d',
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
    padding: '10px 14px',
  },
}));

const StyledBodyRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: 'rgba(11, 30, 61, 0.05)',
    transition: 'background-color 0.15s',
  },
}));

/**
 * DataTable — generic paginated table with search.
 *
 * Props:
 *   title           string                      — optional heading above toolbar
 *   columns         { key, label, render?, align?, minWidth? }[]
 *   rows            object[]                    — raw data array
 *   renderActions   (row) => ReactNode          — per-row action buttons
 *   toolbarActions  ReactNode                   — buttons rendered on the right of toolbar
 *   loading         boolean                     — show skeleton placeholder rows
 *   searchable      boolean (default true)      — show search input
 *   rowsPerPageOptions number[] (default [10,25,50])
 *   getRowKey       (row, index) => string|number
 */
export default function DataTable({
  title,
  columns = [],
  rows = [],
  renderActions,
  toolbarActions,
  loading = false,
  searchable = true,
  rowsPerPageOptions = [10, 25, 50],
  getRowKey,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [search, setSearch] = useState('');

  useEffect(() => { setPage(0); }, [search, rows.length]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(row =>
      columns.some(col => {
        const val = row[col.key];
        return val != null && String(val).toLowerCase().includes(q);
      })
    );
  }, [rows, search, columns]);

  const paginatedRows = filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const colSpan = columns.length + (renderActions ? 1 : 0);
  const skeletonCount = rowsPerPage > 10 ? 10 : rowsPerPage;

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          flexWrap: 'wrap',
          gap: 1.5,
          background: '#fafbfc',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {title && (
            <Typography variant="subtitle1" fontWeight={700} color="#0b1e3d">
              {title}
            </Typography>
          )}
          {searchable && (
            <TextField
              size="small"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 200,
                '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.82rem' },
              }}
            />
          )}
        </Box>
        {toolbarActions && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {toolbarActions}
          </Box>
        )}
      </Box>

      {/* Table */}
      <TableContainer>
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <StyledHeaderCell
                  key={col.key}
                  align={col.align || 'left'}
                  style={col.minWidth ? { minWidth: col.minWidth } : undefined}
                >
                  {col.label}
                </StyledHeaderCell>
              ))}
              {renderActions && (
                <StyledHeaderCell align="center">Action</StyledHeaderCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: skeletonCount }).map((_, i) => (
                <StyledBodyRow key={i}>
                  {Array.from({ length: colSpan }).map((__, j) => (
                    <TableCell key={j} sx={{ py: 1.2 }}>
                      <Skeleton variant="text" width="80%" height={18} />
                    </TableCell>
                  ))}
                </StyledBodyRow>
              ))
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ py: 6, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: 'text.disabled' }}>
                    <InboxIcon sx={{ fontSize: 40 }} />
                    <Typography variant="body2">No records found</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, index) => (
                <StyledBodyRow key={getRowKey ? getRowKey(row, index) : (row.id ?? index)}>
                  {columns.map(col => (
                    <TableCell
                      key={col.key}
                      align={col.align || 'left'}
                      sx={{ fontSize: '0.82rem', py: 1, px: '14px' }}
                    >
                      {col.render ? col.render(row) : (row[col.key] ?? '')}
                    </TableCell>
                  ))}
                  {renderActions && (
                    <TableCell align="center" sx={{ py: 0.5, px: '14px', whiteSpace: 'nowrap' }}>
                      {renderActions(row)}
                    </TableCell>
                  )}
                </StyledBodyRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={rowsPerPageOptions}
        sx={{
          borderTop: '1px solid #e2e8f0',
          fontSize: '0.8rem',
          '& .MuiTablePagination-toolbar': { minHeight: 44 },
        }}
      />
    </Paper>
  );
}
